import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { updateCanvas } from 'utils';
import constants from 'constants';
import { useDispatch, useSelector } from 'react-redux';

const { CANVAS_UPDATE_TYPES, CANVAS_UPDATE_METHODS } = constants;

const ZoomContainer = styled.div`
  width: 100%;
  height: 100%;
  touch-action: none;
`;

const DisableContainer = styled.div`
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export function PreviewWrapper({ children, modelId = 0 }) {
  const containerRef = useRef(null);
  const [ showModal, setShowModal ] = useState(false);
  const isPressed = useRef(false);
  const dispatch = useDispatch();

    const sceneData = useSelector(state => state.canvas.serializedData);

  useEffect(() => {
    if (sceneData) {
       setShowModal(true);
    }
  }, [ sceneData ]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const stop = e => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };

    const opts = { passive: false, capture: true };

    el.addEventListener('touchmove', stop, opts);

    el.addEventListener('gesturestart', stop, opts);
    el.addEventListener('gesturechange', stop, opts);

    return () => {
      el.removeEventListener('touchmove', stop, opts);
      el.removeEventListener('gesturestart', stop, opts);
      el.removeEventListener('gesturechange', stop, opts);
    };
  }, []);
  const rotateArgs = {
    dispatch,
    type: CANVAS_UPDATE_TYPES.MESH,
    method: CANVAS_UPDATE_METHODS.UPDATE,
  };

  // refs for target vs. current rotation (in degrees)
  const target = useRef({ x: 0, y: 0 });
  const current = useRef({ x: 0, y: 0 });
  const raf = useRef(null);

  const doRotate = (rx, ry, load) => {
    if (load == false || isPressed.current) {
      // still loading
      return;
    }
    updateCanvas(rotateArgs, {
      id: modelId,
      globalTransform: true,
      transforms: {
        rx: rx,   // assume your mesh system takes degrees
        ry: ry,
        rz: 0,
      },
    });
  };

  useEffect(() => {
    const animate = () => {
      // smoothing factor: 0.3 → 30% of gap per frame
      const stiffness = 0.3;

      // lerp current toward target
      current.current.x += (target.current.x - current.current.x) * stiffness;
      current.current.y += (target.current.y - current.current.y) * stiffness;

      // do rotate only for y axis
      doRotate(0, -current.current.y, showModal);

      raf.current = requestAnimationFrame(animate);
    };

    // kick off the loop
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [ showModal ]); // run once on mount

  // capture mouse moves into target
  const handleMouseMove = e => {
    if (isPressed.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const nx = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
    const ny = (e.clientY - (rect.top  + rect.height / 2)) / (rect.height / 2);
    const maxDeg = 60;

    target.current.x = -ny * maxDeg;
    target.current.y =  nx * maxDeg;
  };

  return (
    <ZoomContainer
      ref={containerRef}
      onMouseMove={handleMouseMove}
    >
      <DisableContainer>
      {children}
      </DisableContainer>
    </ZoomContainer>
  );
}

export function DisableZoomContainer({ children }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const stop = e => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };

    const opts = { passive: false, capture: true };

    el.addEventListener('wheel', stop, opts);

    el.addEventListener('touchmove', stop, opts);

    el.addEventListener('gesturestart', stop, opts);
    el.addEventListener('gesturechange', stop, opts);

    return () => {
      el.removeEventListener('wheel', stop, opts);
      el.removeEventListener('touchmove', stop, opts);
      el.removeEventListener('gesturestart', stop, opts);
      el.removeEventListener('gesturechange', stop, opts);
    };
  }, []);

  return <ZoomContainer ref={containerRef}>{children}</ZoomContainer>;
}

